import {
  faChevronDown,
  faChevronUp,
  faPenToSquare
} from '@fortawesome/free-solid-svg-icons';
import { faSquarePlus } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Flex, Table } from 'components';
import { ActiveDot } from 'components/ActiveDot/ActiveDot';
import { ExecutionProgressTracking } from 'components/ExecutionProgressTracking/ExecutionProgressTracking';
import { Button, SwitchButton } from 'components/_form';
import { useAdminTaskTypeContext } from 'contexts/AdminTaskTypeContext';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { ITaskType } from 'types/taskType';
import { TaskTypeBadge } from '../TaskTypeBadge/TaskTypeBadge';

const Buttons = styled(Flex)`
  width: fit-content;
  margin: 0 auto;
  gap: 8px;
  align-items: center;
`;

const MoveButtons = styled(Flex)`
  flex-direction: column;
  gap: 2px;
`;

const CenteredCell = styled(Flex)`
  justify-content: center;
  align-items: center;
  width: 100%;
`;

const StatusCell = styled(CenteredCell)`
  & > * {
    width: fit-content;
    max-width: 100%;
  }
`;

const StepLetter = styled.strong`
  font-size: 1.125rem;
  font-weight: 800;
  line-height: 1;
`;

interface Props {
  taskTypes: ITaskType[];
  onEdit: (taskType: ITaskType) => void;
  onAddSubType: (parentId: number) => void;
  onChangeOrder: (taskType: ITaskType, type: 'increase' | 'decrease') => void;
}

export const TaskTypesTable: React.FC<Props> = ({
  taskTypes,
  onEdit,
  onAddSubType,
  onChangeOrder
}) => {
  const { t } = useTranslation();
  const { userFunctionsList } = useAdminTaskTypeContext();

  const centeredColumnKeys = [0, 2, 3, 4, 5, 6];

  const header = [
    t('administrationTasksView.taskTypeTableHeaders.active'),
    t('administrationTasksView.taskTypeTableHeaders.taskType'),
    t('administrationTasksView.taskTypeTableHeaders.status'),
    t('administrationTasksView.taskTypeTableHeaders.requiresApproval'),
    t('administrationTasksView.taskTypeTableHeaders.stepsToApprove'),
    t('administrationTasksView.taskTypeTableHeaders.whoApproves'),
    ''
  ];

  const data = useMemo(() => {
    const buildRow = (
      taskType: ITaskType,
      listLength: number,
      parentColorCode?: string
    ) => {
      const isChild = !!taskType.parent_id;

      const stepsToApprove = taskType.approvers
        ?.flatMap(({ execution_progress_tracking_approval_steps }) =>
          execution_progress_tracking_approval_steps.flatMap(
            ({ steps_to_approve }) => steps_to_approve
          )
        )
        .filter(Boolean);

      const approverRoles = taskType.approvers
        ?.map(
          ({ function_id }) =>
            userFunctionsList.find(
              (userFunction) =>
                userFunction.value?.toString() === function_id?.toString()
            )?.label
        )
        .filter(Boolean);

      return {
        ...(isChild && {
          __rowLeftAccent: parentColorCode || '#d9d9d9'
        }),
        active: (
          <CenteredCell key={`active-${taskType.id}`}>
            <ActiveDot active={taskType.active} />
          </CenteredCell>
        ),
        taskType: (
          <TaskTypeBadge
            key={`taskType-${taskType.id}`}
            color_code={taskType.color_code}
            color_icon={taskType.color_icon}
            name={taskType.name}
            isSubType={isChild}
          />
        ),
        status: taskType.execution_progress_tracking ? (
          <StatusCell key={`status-${taskType.id}`}>
            <ExecutionProgressTracking
              activeAll
              executionProgressTracking={taskType.execution_progress_tracking}
            />
          </StatusCell>
        ) : null,
        requiresApproval: (
          <CenteredCell key={`approval-${taskType.id}`}>
            <SwitchButton
              disabled
              value={!!taskType.requires_approval}
              withoutMargin
            />
          </CenteredCell>
        ),
        stepsToApprove: (
          <CenteredCell gap="6px" flexWrap="wrap" key={`steps-${taskType.id}`}>
            {[...new Set(stepsToApprove)].map((step) => (
              <StepLetter key={step}>{step}</StepLetter>
            ))}
          </CenteredCell>
        ),
        whoApproves: (
          <CenteredCell key={`who-${taskType.id}`}>
            {[...new Set(approverRoles)].join(', ')}
          </CenteredCell>
        ),
        actions: (
          <Buttons key={`actions-${taskType.id}`}>
            <Button
              variant="eucalyptus"
              icon={<FontAwesomeIcon icon={faPenToSquare} />}
              onClick={() => onEdit(taskType)}
            />
            {!taskType.parent_id && (
              <Button
                variant="green"
                icon={<FontAwesomeIcon icon={faSquarePlus} />}
                onClick={() => onAddSubType(taskType.id)}
              />
            )}
            <MoveButtons>
              <Button
                bordered
                transparent
                icon={<FontAwesomeIcon icon={faChevronUp} />}
                minHeight="17px"
                disabled={taskType.order <= 1}
                onClick={() => onChangeOrder(taskType, 'decrease')}
              />
              <Button
                bordered
                transparent
                icon={<FontAwesomeIcon icon={faChevronDown} />}
                minHeight="17px"
                disabled={taskType.order >= listLength}
                onClick={() => onChangeOrder(taskType, 'increase')}
              />
            </MoveButtons>
          </Buttons>
        )
      };
    };

    const parents = taskTypes
      .filter(({ parent_id }) => !parent_id)
      .sort((a, b) => (a.order || 0) - (b.order || 0));

    return parents.flatMap((parent) => {
      const subList = taskTypes
        .filter(({ parent_id }) => parent_id === parent.id)
        .sort((a, b) => (a.order || 0) - (b.order || 0));

      const parentRow = buildRow(parent, parents.length);

      const childRows = subList.map((subTaskType) =>
        buildRow(subTaskType, subList.length, parent.color_code)
      );

      return [parentRow, ...childRows];
    });
  }, [taskTypes, userFunctionsList, onEdit, onAddSubType, onChangeOrder]);

  return (
    <Table
      header={header}
      data={data}
      sortTable={false}
      leftFixedKeys={[0, 1]}
      rightFixedKeys={[header.length - 1]}
      centeredColumnKeys={centeredColumnKeys}
    />
  );
};
